// An object's name as the way to it (#263). Wherever a page names an access
// point or a switch, a station or an SSID, the name is a link: a click opens
// the page that owns that kind of object, narrowed to exactly it — the
// device on Devices, the station on Clients, the SSID on Networks — at the
// scope being looked at. A device named on a Design page opens Design ›
// Physical › Equipment instead, whose drawer links on to Devices (#1888).
// A ⌘-click (Ctrl on Windows and Linux) stays on the page being read and
// adds the object to its context, keeping the facets
// already there, so "this station, on this access point, on this SSID" is
// three such clicks and one address. On a page that does not understand the
// object's facet the modifier means nothing and the click navigates.
//
// The modifier is a trade: ⌘-click on these names no longer opens a new tab.
// Every other modifier has a browser meaning on a link too, and the gesture
// is the one #263 asked for; an affordance beside the name is the fallback if
// this proves wrong, and a follow-on.
import type { MouseEvent, ReactNode } from "react";
import { useLocation, type LinkProps } from "react-router-dom";
import { facetsOf, homeOf, useFacets, type ObjectKind } from "./facets";
import { ScopeLink } from "./scope";

const OPENS: Record<ObjectKind, string> = {
  device: "Open this device",
  station: "Only this client",
  ssid: "The network carrying this SSID",
};

/** `value` is the object's identifier in the facet's own spelling: a serial,
 * a station's MAC as twelve bare hex digits, an SSID. `children` is how it
 * is shown; the value itself when there is none. */
export function ObjectLink({ kind, value, children, title, onClick, ...rest }: Omit<LinkProps, "to" | "title"> & { kind: ObjectKind; value: string; title?: string; children?: ReactNode }) {
  const { pathname } = useLocation();
  // A device named on a Design page opens in Design (#1888, `homeOf`).
  const { facet, page } = homeOf(kind, pathname);
  const { set } = useFacets();
  const here = facetsOf(pathname).includes(facet);
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || !here || !(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    set({ [facet]: value });
  };
  const hint = (title ?? OPENS[kind]) + (here ? ". ⌘-click narrows this page to it." : "");
  return (
    <ScopeLink to={page} facets={{ [facet]: value }} title={hint} onClick={handle} {...rest}>{children ?? value}</ScopeLink>
  );
}
